import * as React from "react"
import {useCallback, useState, useEffect, useRef} from "react"
import {Box, Grid, Typography} from "@mui/material";
import {ButtonLoadingSeeker, CardSeeker, IconSeeker} from "@ui-seeker/core";
import {useAppToast} from "@ui-seeker/app";
import {postSubmit} from "@ui-seeker/utils";

import {BACKEND_APIS} from "@constants";
import {MatchersLoadingSkuBlock} from "../../@components";
import useReverseMatcherTableItems from "../@hooks/useTableItems";
import ReverseMatcherTableRow from "./TableRow";
import ReverseMatcherImageComparisonModal from "../Options/ImageComparisonModal";


export default function ReverseMatcherTableView({ onEditItem, exposeRemoveItem }) {
	const { showToast } = useAppToast()
	const { items, isLoading, total, hasMore, loadMore, removeItem } = useReverseMatcherTableItems()
	const [validatingItems, setValidatingItems] = useState(new Set())
	const scrollContainerRef = useRef(null)

	// Exponer removeItem al componente padre
	useEffect(() => {
		if (exposeRemoveItem) {
			exposeRemoveItem(removeItem)
		}
	}, [removeItem, exposeRemoveItem])

	const isItemValidating = useCallback((mappingID) => {
		return validatingItems.has(mappingID)
	}, [validatingItems])

	const setItemValidating = useCallback((mappingID, isValidating) => {
		setValidatingItems(prev => {
			const newSet = new Set(prev)
			if (isValidating) {
				newSet.add(mappingID)
			} else {
				newSet.delete(mappingID)
			}
			return newSet
		})
	}, [])

	const scrollToTop = useCallback(() => {
		if (scrollContainerRef.current) {
			scrollContainerRef.current.scrollTo({
				top: 0,
				behavior: 'smooth'
			})
		}
	}, [])

	const handleValidate = useCallback((item) => {
		if (!item.aiCandidate) return

		setItemValidating(item.mappingID, true)
		scrollToTop()

		const productID = item.aiCandidate.productID

		// Single API call - backend handles SKU_MAPPING update via ia-normalizer
		postSubmit(`${BACKEND_APIS.MATCHERS}/reverse/mapping-product`, {
			productID: productID,
			mappingID: item.mappingID,
			siteID: item.siteID,
			skuID: item.skuID,
			brandID: item.brandID,
			productURL: "",
			notFound: false,
			qa: true
		}).then(() => {
			showToast.success(`SKU ${item.skuID} mapped successfully!`, {
				position: "top-right"
			})
			removeItem(item.mappingID)
			setItemValidating(item.mappingID, false)
		}).catch(e => {
			console.error('ERROR MAPPING SKU', e)
			showToast.error(`Failed to map SKU ${item.skuID}`, {
				position: "top-left"
			})
			setItemValidating(item.mappingID, false)
		})
	}, [removeItem, showToast, scrollToTop])

	const handleNotFound = useCallback((item) => {
		setItemValidating(item.mappingID, true)
		scrollToTop()

		postSubmit(`${BACKEND_APIS.MATCHERS}/reverse/mapping-product`, {
			productID: null,
			mappingID: item.mappingID,
			siteID: item.siteID,
			skuID: item.skuID,
			brandID: item.brandID,
			productURL: "",
			notFound: true,
			qa: true
		}).then(() => {
			showToast.info(`SKU ${item.skuID} marked as not found`, {
				position: "top-right"
			})
			removeItem(item.mappingID)
			setItemValidating(item.mappingID, false)
		}).catch(e => {
			console.error('ERROR MARKING SKU AS NOT FOUND', e)
			showToast.error(`Failed to mark SKU ${item.skuID} as not found`, {
				position: "top-left"
			})
			setItemValidating(item.mappingID, false)
		})
	}, [removeItem, showToast, scrollToTop])

	const handleEdit = useCallback((item) => {
		if (onEditItem) {
			onEditItem(item)
		}
	}, [onEditItem])

	if (isLoading && items.length === 0) {
		return <CardSeeker>
			<MatchersLoadingSkuBlock />
		</CardSeeker>
	}

	return <Box>
		<CardSeeker sx={{ mb: 1, px: 2, py: 1 }}>
			<Box display="flex" justifyContent="space-between" alignItems="center">
				<Box display="flex" gap={2} alignItems="center">
					<Typography variant="h6" fontWeight="bold">
						Batch Mapping View
					</Typography>
					<Typography variant="body2" color="text.secondary">
						({items.length} / {total} items)
					</Typography>
				</Box>
				<Box display="flex" gap={1} alignItems="center">
					<IconSeeker icon="table-list" sx={{ fontSize: "1.2rem", color: 'primary.main' }} />
				</Box>
			</Box>
		</CardSeeker>

		{/* Column Headers */}
		<Grid container spacing={2} px={2} mb={1}>
			<Grid item xs={6}>
				<Typography variant="h6" color="primary" fontWeight="bold">
					SKU to Map
				</Typography>
			</Grid>
			<Grid item xs={6}>
				<Typography variant="h6" color="success.main" fontWeight="bold">
					AI Candidate
				</Typography>
			</Grid>
		</Grid>

		<Box ref={scrollContainerRef} sx={{ maxHeight: "75vh", overflowY: "auto", pr: 1 }}>
			{items.map(item => (
				<ReverseMatcherTableRow
					key={item.mappingID}
					item={item}
					onValidate={handleValidate}
					onNotFound={handleNotFound}
					onEdit={handleEdit}
					isValidating={isItemValidating(item.mappingID)}
				/>
			))}

			{hasMore && (
				<Box display="flex" justifyContent="center" mt={2} mb={2}>
					<ButtonLoadingSeeker
						loading={isLoading}
						onClick={loadMore}
						startIcon={<IconSeeker icon="arrow-down" />}
					>
						Load More ({total - items.length} remaining)
					</ButtonLoadingSeeker>
				</Box>
			)}

			{!hasMore && items.length > 0 && (
				<Box display="flex" justifyContent="center" mt={2} mb={2}>
					<Typography variant="body2" color="text.secondary">
						All items loaded
					</Typography>
				</Box>
			)}

			{items.length === 0 && !isLoading && (
				<CardSeeker>
					<Box display="flex" flexDirection="column" alignItems="center" gap={2} p={4}>
						<IconSeeker icon="circle-check" sx={{ fontSize: "4rem", color: 'success.main' }} />
						<Typography variant="h5">
							No pending mappings
						</Typography>
						<Typography variant="body2" color="text.secondary">
							All items have been processed
						</Typography>
					</Box>
				</CardSeeker>
			)}
		</Box>

		<ReverseMatcherImageComparisonModal
			brandOptions={[]}
			setSelectedSKU={() => {}}
			saveMapping={null}
		/>
	</Box>
}
